import { useState, useEffect, useRef } from "react";
import { defaultFilters } from "../constants";
import { useUXContext } from "../components/UXContext";
import { resetWebfuseSession } from "../utils/webfuse";

const getFromStorage = (key, fallback) => {
  try {
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : fallback;
  } catch {
    return fallback;
  }
};

const usePersistentState = (key, fallback) => {
  const [state, setState] = useState(() => getFromStorage(key, fallback));

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
};

export default function useCache() {
  const sessionId = localStorage.getItem("newsCompare:currentSessionId") || null;

  const { mode } = useUXContext();
  const isTop = mode === "top";

  const [articlesTop, setArticlesTop] = usePersistentState("newsCompare:topHeadlines", []);
  const [articlesSearch, setArticlesSearch] = usePersistentState("newsCompare:detailedSearch", []);

  const [selectedTop, setSelectedTop] = usePersistentState("newsCompare:selectedTop", null);
  const [selectedSearch, setSelectedSearch] = usePersistentState("newsCompare:selectedSearch", null);

  const [filtersTop, setFiltersTop] = usePersistentState("newsCompare:filtersTop", defaultFilters);
  const [filtersSearch, setFiltersSearch] = usePersistentState("newsCompare:filtersSearch", defaultFilters);

  const [readerMode, setReaderMode] = usePersistentState("newsCompare:readerMode", true);
  const [currentMode, setCurrentMode] = usePersistentState("newsCompare:mode", "top");

  const [sentimentCache, setSentimentCache] = usePersistentState(
    "newsCompare:sentimentCache",
    {} // { [hash]: analysisObject }
  );
  const [lastSentimentHash, setLastSentimentHash] = usePersistentState(
    "newsCompare:lastSentimentHash",
    null
  );

  // --- Logging helper: after a clear, log the NEW values once state has committed ---
  const postClearLogRef = useRef(false);
  useEffect(() => {
    if (!postClearLogRef.current) return;

    const STORAGE_KEYS = [
      "newsCompare:topHeadlines",
      "newsCompare:detailedSearch",
      "newsCompare:selectedTop",
      "newsCompare:selectedSearch",
      "newsCompare:filtersTop",
      "newsCompare:filtersSearch",
      "newsCompare:readerMode",
      "newsCompare:lastPostedArticle",
      "newsCompare:sentimentCache",
      "newsCompare:lastSentimentHash",
      "newsCompare:mode",
    ];

    const storageSnapshot = STORAGE_KEYS.reduce((acc, k) => {
      acc[k] = localStorage.getItem(k);
      return acc;
    }, {});

    console.groupCollapsed("🧹 Cache cleared → NEW values committed");
    console.log("state:", {
      articlesTop,
      articlesSearch,
      selectedTop,
      selectedSearch,
      filtersTop,
      filtersSearch,
      readerMode,
      sentimentCache,
      lastSentimentHash,
      mode: currentMode,
    });
    console.log("localStorage:", storageSnapshot);
    console.groupEnd();

    postClearLogRef.current = false; // log only once per clear
  }, [
    articlesTop,
    articlesSearch,
    selectedTop,
    selectedSearch,
    filtersTop,
    filtersSearch,
    readerMode,
    sentimentCache,
    lastSentimentHash,
    currentMode,
  ]);

  const clearAllCache = () => {
    localStorage.removeItem("newsCompare:topHeadlines");
    localStorage.removeItem("newsCompare:detailedSearch");
    localStorage.removeItem("newsCompare:selectedTop");
    localStorage.removeItem("newsCompare:selectedSearch");
    localStorage.removeItem("newsCompare:filtersTop");
    localStorage.removeItem("newsCompare:filtersSearch");
    localStorage.removeItem("newsCompare:readerMode");
    localStorage.removeItem("newsCompare:lastPostedArticle");
    localStorage.removeItem("newsCompare:sentimentCache");
    localStorage.removeItem("newsCompare:lastSentimentHash");

    setArticlesTop([]);
    setArticlesSearch([]);
    setSelectedTop(null);
    setSelectedSearch(null);
    setFiltersTop(defaultFilters);
    setFiltersSearch(defaultFilters);
    setReaderMode(true);
    setSentimentCache({});
    setLastSentimentHash(null);
    resetWebfuseSession(sessionId);

    // Signal listeners and request a post-commit log
    window.dispatchEvent(new CustomEvent("newscompare:cache-cleared"));
    postClearLogRef.current = true;
  };

  return {
    sentimentCache,
    setSentimentCache,
    lastSentimentHash,
    setLastSentimentHash,

    articles: isTop ? articlesTop : articlesSearch,
    setArticles: isTop ? setArticlesTop : setArticlesSearch,

    selected: isTop ? selectedTop : selectedSearch,
    setSelected: isTop ? setSelectedTop : setSelectedSearch,

    filters: isTop ? filtersTop : filtersSearch,
    setFilters: isTop ? setFiltersTop : setFiltersSearch,

    readerMode,
    setReaderMode,

    mode: currentMode,
    setMode: setCurrentMode,

    clearAllCache,
  };
}
